import { latLngToH3 } from '@repo/geo'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/components/ui/avatar'
import { Button } from '@repo/ui/components/ui/button'
import { CardContent } from '@repo/ui/components/ui/card'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { Switch } from '@repo/ui/components/ui/switch'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { createFileRoute } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  ClipboardList,
  Loader2,
  MapPin,
  Phone,
  ShieldCheck,
} from 'lucide-react'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'
import {
  BookingCompletion,
  ServiceSelectionStage,
  StorefrontHero,
} from '@/components/booking/storefront-layouts'
import { MarkdownPreview } from '@/components/ui/markdown-preview'
import { PremiumPanel } from '@/components/ui/premium-panel'
import {
  areRequiredBookingResponsesComplete,
  canLookupFeasibleSlots,
  getSlotLookupRecoveryState,
} from '@/lib/booking-flow'
import {
  type DisplayGroupBookingPageConfig,
  getBookingPolicyItems,
  getConfirmationContent,
  normalizeDisplayGroupBookingConfig,
  normalizeOperatorBookingTheme,
  normalizeServiceBookingConfig,
  type OperatorBookingPageTheme,
  resolveServicePriceDisplay,
  type ServiceBookingPageConfig,
  type ServicePriceDisplay,
} from '@/lib/booking-page-customization'
import { formatPhoneDisplay, formatPhoneInput, isValidPhoneInput } from '@/lib/format-phone'
import { trpc } from '@/lib/trpc'

export const Route = createFileRoute('/book/$operatorSlug')({
  component: BookingPage,
})

type Service = {
  id: string
  name: string
  slug: string
  displayGroupId: string | null
  description: string | null
  durationMinutes: number
  price: number | null
  allowQuantity: boolean
  quantityLabel: string | null
  maxQuantity: number | null
  bookingPageConfig?: ServiceBookingPageConfig | null
}

type FormField = {
  id: string
  fieldType: string
  label: string
  placeholder: string | null
  helpText: string | null
  isRequired: boolean
  options: string[] | null
  sortOrder: number
}

type BookingOperator = {
  businessName: string
  slug: string
  bio: string | null
  phone: string | null
  logoUrl: string | null
  bookingPageTheme: OperatorBookingPageTheme | null
}

type BookingMenu = {
  bookingPageConfig?: unknown
}

type DisplayGroup = {
  id: string
  bookingPageConfig?: DisplayGroupBookingPageConfig | null
}

type PublicBookingData = {
  operator: BookingOperator
  menu: BookingMenu | null
  displayGroups: DisplayGroup[]
  services: Service[]
  previewMode: boolean
}

type BookingStep = 'services' | 'location' | 'schedule' | 'contact' | 'confirmed'

const STEP_ORDER_BY_FLOW: Record<
  OperatorBookingPageTheme['flowStyle'],
  Exclude<BookingStep, 'confirmed'>[]
> = {
  menu_first: ['services', 'location', 'schedule', 'contact'],
  address_first: ['location', 'services', 'schedule', 'contact'],
}

function BookingPage() {
  const { operatorSlug } = Route.useParams()
  const query = trpc.operator.getBySlug.useQuery({ slug: operatorSlug }, { retry: false })

  return (
    <BookingExperience
      operatorSlug={operatorSlug}
      data={query.data as PublicBookingData | undefined}
      isLoading={query.isLoading}
      error={query.error}
    />
  )
}

export function CatalogPreviewBookingPage({
  catalogId,
  themeOverride,
}: {
  catalogId: string
  themeOverride?: OperatorBookingPageTheme | null
}) {
  const query = trpc.operator.getBySlug.useQuery({ catalogId }, { retry: false })

  return (
    <BookingExperience
      operatorSlug={(query.data as PublicBookingData | undefined)?.operator.slug ?? ''}
      data={query.data as PublicBookingData | undefined}
      isLoading={query.isLoading}
      error={query.error}
      themeOverride={themeOverride}
    />
  )
}

function BookingExperience({
  operatorSlug,
  data,
  isLoading,
  error,
  themeOverride,
}: {
  operatorSlug: string
  data: PublicBookingData | undefined
  isLoading: boolean
  error: unknown
  themeOverride?: OperatorBookingPageTheme | null
}) {
  const [step, setStep] = useState<BookingStep>('services')
  const [selectedServices, setSelectedServices] = useState<Service[]>([])
  const primaryService = selectedServices[0] ?? null

  const [notes, setNotes] = useState('')
  const [formResponses, setFormResponses] = useState<Record<string, unknown>>({})
  const [isFlexible, setIsFlexible] = useState(false)

  const [addressQuery, setAddressQuery] = useState('')
  const [addressSuggestions, setAddressSuggestions] = useState<
    Array<{ placeId: string; displayName: string; lat: number; lng: number; h3Index?: string }>
  >([])
  const [selectedAddress, setSelectedAddress] = useState<{
    displayName: string
    lat: number
    lng: number
    h3Index: string
    postalCode?: string | null
  } | null>(null)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')

  const [customerName, setCustomerName] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [verificationToken, setVerificationToken] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const isCustomerPhoneValid = isValidPhoneInput(customerPhone)

  const [bookingResult, setBookingResult] = useState<{
    id: string
    date: string
    startTime: string
    endTime: string
  } | null>(null)

  const totalDurationMinutes = selectedServices.reduce(
    (total, service) => total + service.durationMinutes,
    0
  )
  const totalPrice = selectedServices.reduce((total, service) => total + (service.price ?? 0), 0)
  const dateOptions = useMemo(() => buildDateOptions(21), [])

  const formFieldsQuery = trpc.services.formFields.listByService.useQuery(
    { serviceId: primaryService?.id ?? '' },
    { enabled: !!primaryService }
  )

  const resolveFeasibilityQuery = trpc.booking.resolveFeasibility.useQuery(
    {
      operatorSlug,
      serviceId: primaryService?.id ?? '',
      addressText: selectedAddress?.displayName,
      lat: selectedAddress?.lat ?? 0,
      lng: selectedAddress?.lng ?? 0,
      h3Index: selectedAddress?.h3Index || undefined,
      postalCode: selectedAddress?.postalCode ?? undefined,
    },
    { enabled: Boolean(primaryService && selectedAddress?.h3Index) }
  )
  const feasibility = resolveFeasibilityQuery.data ?? null

  const feasibleSlotsQuery = trpc.booking.getFeasibleSlots.useQuery(
    {
      operatorSlug,
      serviceId: primaryService?.id ?? '',
      date: selectedDate,
      quantity: 1,
      serviceDurationMinutesOverride: totalDurationMinutes || undefined,
      timezoneOffsetMinutes: new Date().getTimezoneOffset() * -1,
      customerH3: feasibility?.bookingLocation.h3Index ?? '',
      postalCode: feasibility?.bookingLocation.postalCode ?? undefined,
    },
    {
      enabled: canLookupFeasibleSlots({
        serviceId: primaryService?.id,
        date: selectedDate,
        slotLookupAllowed: feasibility?.slotLookupAllowed,
        customerH3: feasibility?.bookingLocation.h3Index,
      }),
    }
  )

  const sendOTPMutation = trpc.booking.sendOTP.useMutation({
    onSuccess: () => setOtpSent(true),
  })
  const verifyOTPMutation = trpc.booking.verifyOTP.useMutation({
    onSuccess: (result) => setVerificationToken(result.token),
  })
  const createAppointmentMutation = trpc.booking.createAppointment.useMutation({
    onSuccess: (result) => {
      setBookingResult(result)
      setStep('confirmed')
    },
  })

  useEffect(() => {
    if (addressQuery.length < 3) {
      setAddressSuggestions([])
      return
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQuery(addressQuery), 350)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [addressQuery])

  const autocompleteQuery = trpc.geo.autocomplete.useQuery(
    { query: debouncedQuery, limit: 5 },
    { enabled: debouncedQuery.length >= 3 && !selectedAddress }
  )

  useEffect(() => {
    if (autocompleteQuery.data) setAddressSuggestions(autocompleteQuery.data)
  }, [autocompleteQuery.data])

  const geocodeQuery = trpc.geo.geocode.useQuery(
    { address: selectedAddress?.displayName ?? '' },
    { enabled: !!selectedAddress && !selectedAddress.h3Index }
  )

  useEffect(() => {
    if (geocodeQuery.data && selectedAddress && !selectedAddress.h3Index) {
      setSelectedAddress((current) =>
        current
          ? {
              ...current,
              h3Index: geocodeQuery.data.h3Index,
              postalCode: geocodeQuery.data.postalCode,
            }
          : null
      )
    }
  }, [geocodeQuery.data, selectedAddress])

  if (isLoading) {
    return (
      <BookingCanvas>
        <div className="mx-auto w-full max-w-3xl space-y-4 px-5 py-16">
          <div className="h-10 w-64 animate-pulse rounded bg-muted" />
          <div className="h-32 animate-pulse rounded bg-muted" />
        </div>
      </BookingCanvas>
    )
  }

  if (error || !data) {
    const errorCopy = getBookingPageLoadErrorCopy(error)

    return (
      <BookingCanvas>
        <div className="mx-auto max-w-2xl px-5 py-20 text-center">
          <h1 className="text-2xl font-semibold">{errorCopy.title}</h1>
          <p className="mt-2 text-muted-foreground">{errorCopy.description}</p>
        </div>
      </BookingCanvas>
    )
  }

  const { operator, services, previewMode } = data
  const bookingTheme = normalizeOperatorBookingTheme(themeOverride ?? operator.bookingPageTheme)
  const stepOrder = getStepOrder(bookingTheme.flowStyle)
  const renderedStep =
    bookingTheme.flowStyle === 'address_first' &&
    step === 'services' &&
    selectedServices.length === 0 &&
    !selectedAddress
      ? 'location'
      : step
  const serviceStepNext = getNextStep('services', stepOrder)
  const locationStepNext = getNextStep('location', stepOrder)
  const displayGroupById = new Map(
    (data.displayGroups ?? []).map((displayGroup) => [
      displayGroup.id,
      normalizeDisplayGroupBookingConfig(displayGroup.bookingPageConfig),
    ])
  )
  const bookingPolicyItems = getBookingPolicyItems(bookingTheme)
  const serviceList = selectedServices.map((service) => service.name).join(' + ')
  const selectedPriceDisplay = resolveSelectedPriceDisplay(selectedServices, displayGroupById)
  const confirmationContent = getConfirmationContent(bookingTheme, {
    businessName: operator.businessName,
    customerName,
    serviceName: primaryService?.name ?? '',
    serviceList,
    date: bookingResult ? formatDateLabel(bookingResult.date) : selectedDate,
    startTime: bookingResult?.startTime ?? selectedTime,
    endTime: bookingResult?.endTime ?? '',
    address: selectedAddress?.displayName ?? '',
    bookingReference: bookingResult?.id ?? '',
  })
  const serviceFormFields = formFieldsQuery.data ?? []
  const requiredServiceFieldsComplete = areRequiredBookingResponsesComplete(
    serviceFormFields,
    formResponses
  )
  const slotRecoveryState =
    feasibleSlotsQuery.data?.result === 'no_feasible_slots' ||
    feasibleSlotsQuery.data?.result === 'outside_service_area' ||
    feasibleSlotsQuery.data?.result === 'booking_unavailable' ||
    feasibleSlotsQuery.data?.result === 'degraded_unavailable'
      ? getSlotLookupRecoveryState(feasibleSlotsQuery.data.result)
      : null

  function resetSchedule() {
    setSelectedDate('')
    setSelectedTime('')
    setVerificationToken('')
    setOtpSent(false)
    setOtpCode('')
    setBookingResult(null)
  }

  function toggleService(service: Service) {
    setSelectedServices((current) => {
      const exists = current.some((item) => item.id === service.id)
      return exists ? current.filter((item) => item.id !== service.id) : [...current, service]
    })
    resetSchedule()
    setFormResponses({})
  }

  function handleAddressInput(value: string) {
    setAddressQuery(value)
    setAddressSuggestions([])
    setSelectedAddress(null)
    resetSchedule()
  }

  function handleSendOTP() {
    sendOTPMutation.mutate({ phone: customerPhone })
  }

  function handleVerifyOTP() {
    verifyOTPMutation.mutate({ phone: customerPhone, code: otpCode })
  }

  function handleCreateAppointment() {
    if (!primaryService || !verificationToken) return

    const bundleResponses =
      selectedServices.length > 1
        ? {
            _zippityServiceBundle: selectedServices.map((service) => ({
              id: service.id,
              name: service.name,
              durationMinutes: service.durationMinutes,
              price: service.price,
            })),
          }
        : {}

    createAppointmentMutation.mutate({
      operatorSlug,
      serviceId: primaryService.id,
      date: selectedDate,
      startTime: selectedTime,
      quantity: 1,
      serviceDurationMinutesOverride: totalDurationMinutes || undefined,
      timezoneOffsetMinutes: new Date().getTimezoneOffset() * -1,
      notes: notes || undefined,
      formResponses:
        Object.keys(formResponses).length > 0 || selectedServices.length > 1
          ? { ...bundleResponses, ...formResponses }
          : undefined,
      isFlexible,
      customerName,
      customerPhone,
      customerEmail: customerEmail || undefined,
      verificationToken,
      addressText: selectedAddress?.displayName,
      lat: selectedAddress ? String(selectedAddress.lat) : undefined,
      lng: selectedAddress ? String(selectedAddress.lng) : undefined,
      h3Index: selectedAddress?.h3Index,
      postalCode: selectedAddress?.postalCode ?? undefined,
    })
  }

  function goBack() {
    const currentIndex = stepOrder.indexOf(step as Exclude<BookingStep, 'confirmed'>)
    const previous = stepOrder[Math.max(0, currentIndex - 1)]
    if (previous) setStep(previous)
  }

  return (
    <BookingCanvas theme={bookingTheme}>
      {previewMode ? <PreviewRibbon /> : null}
      <div className="relative mx-auto flex min-h-[100dvh] w-full max-w-[var(--booking-layout-max)] flex-col px-4 py-5 md:px-6 md:py-8">
        <main className="flex min-w-0 flex-1 flex-col gap-5">
          {bookingTheme.pageLayout !== 'standard' ? (
            <StorefrontHero operator={operator} theme={bookingTheme} services={services} />
          ) : null}
          <header
            className={`border border-border/70 bg-background/90 p-5 shadow-sm backdrop-blur ${getBookingHeaderClass(
              bookingTheme.surfaceStyle
            )}`}
          >
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:flex-wrap sm:justify-between">
              <div className="flex w-full min-w-0 items-start gap-3 sm:flex-1 sm:gap-4">
                <Avatar
                  className={`size-14 shrink-0 border border-border/70 bg-muted sm:size-16 ${getBookingAvatarClass(
                    bookingTheme.surfaceStyle
                  )}`}
                >
                  <AvatarImage src={operator.logoUrl ?? undefined} alt={operator.businessName} />
                  <AvatarFallback
                    className={`${getBookingAvatarClass(bookingTheme.surfaceStyle)} text-lg font-semibold`}
                  >
                    {getBusinessInitials(operator.businessName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    <CalendarDays className="size-3.5 shrink-0" />
                    <span className="min-w-0 break-words">Book with {operator.businessName}</span>
                  </div>
                  <h1 className="break-words text-3xl font-semibold tracking-tight md:text-4xl">
                    {operator.businessName}
                  </h1>
                  {operator.bio ? (
                    <p className="mt-2 max-w-2xl break-words text-sm leading-6 text-muted-foreground">
                      {operator.bio}
                    </p>
                  ) : null}
                  {bookingTheme.bookingCopy.intro ? (
                    <p
                      className={`mt-3 max-w-2xl break-words border border-primary/15 bg-primary/7 px-3 py-2 text-sm leading-6 text-foreground ${getBookingSmallSurfaceClass(
                        bookingTheme.surfaceStyle
                      )}`}
                    >
                      {bookingTheme.bookingCopy.intro}
                    </p>
                  ) : null}
                </div>
              </div>
              {operator.phone ? (
                <a
                  href={`tel:${operator.phone}`}
                  className={`inline-flex items-center gap-2 border border-border/70 bg-card/70 px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground sm:self-start ${getBookingSmallSurfaceClass(
                    bookingTheme.surfaceStyle
                  )}`}
                >
                  <Phone className="size-3.5" />
                  {formatPhoneDisplay(operator.phone)}
                </a>
              ) : null}
            </div>
          </header>

          <div className="flex min-h-[32rem] flex-1 flex-col">
            {renderedStep === 'services' ? (
              <ServiceSelectionStage
                services={services}
                selectedServices={selectedServices}
                theme={bookingTheme}
                displayGroupById={displayGroupById}
                onToggleService={toggleService}
                renderActions={() => (
                  <StepActions
                    backLabel={bookingTheme.flowStyle === 'address_first' ? 'Back' : null}
                    nextLabel={
                      bookingTheme.flowStyle === 'address_first'
                        ? 'See availability'
                        : 'Continue to location'
                    }
                    nextDisabled={
                      selectedServices.length === 0 ||
                      (bookingTheme.flowStyle === 'address_first' &&
                        !feasibility?.slotLookupAllowed)
                    }
                    onBack={goBack}
                    onNext={() => setStep(serviceStepNext)}
                    services={selectedServices}
                    totalPrice={totalPrice}
                    priceDisplay={selectedPriceDisplay}
                    date={selectedDate}
                    time={selectedTime}
                    surfaceStyle={bookingTheme.surfaceStyle}
                  />
                )}
              />
            ) : null}

            {renderedStep === 'location' ? (
              <FlowPanel
                kicker="Service location"
                title="Where should we come?"
                surfaceStyle={bookingTheme.surfaceStyle}
              >
                <div className="space-y-3">
                  <Label htmlFor="service-address">Service address</Label>
                  <Input
                    id="service-address"
                    value={selectedAddress ? selectedAddress.displayName : addressQuery}
                    onChange={(event) => handleAddressInput(event.target.value)}
                    placeholder="Start typing an address"
                    className="h-12 text-base"
                  />
                </div>

                {!selectedAddress && addressSuggestions.length > 0 && addressQuery.length >= 3 ? (
                  <div className="rounded-[8px] border border-border/70 bg-card/65 p-2">
                    {addressSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.placeId}
                        type="button"
                        className="flex w-full cursor-pointer items-center gap-3 rounded-[6px] px-3 py-3 text-left text-sm transition-colors hover:bg-muted"
                        onClick={() => {
                          setSelectedAddress({
                            displayName: suggestion.displayName,
                            lat: suggestion.lat,
                            lng: suggestion.lng,
                            h3Index:
                              suggestion.h3Index ?? latLngToH3(suggestion.lat, suggestion.lng),
                          })
                          setAddressSuggestions([])
                          setAddressQuery('')
                        }}
                      >
                        <MapPin className="size-4 shrink-0 text-primary" />
                        <span className="min-w-0 truncate">{suggestion.displayName}</span>
                      </button>
                    ))}
                  </div>
                ) : null}

                {selectedAddress ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 rounded-[8px] border border-border/70 bg-muted/30 p-3 text-sm">
                      <MapPin className="size-4 shrink-0 text-primary" />
                      <span className="min-w-0 flex-1 truncate">{selectedAddress.displayName}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAddressInput('')}
                      >
                        Change
                      </Button>
                    </div>

                    {resolveFeasibilityQuery.isLoading ? (
                      <InlineStatus icon="loading">Checking address...</InlineStatus>
                    ) : null}

                    {feasibility?.serviceArea.zone === 'out_of_area' ? (
                      <div className="rounded-[8px] border border-destructive/35 bg-destructive/8 p-3 text-sm text-destructive">
                        This address is outside the service area.
                      </div>
                    ) : null}

                    {feasibility?.serviceArea.zone === 'soft_zone' ? (
                      <div className="rounded-[8px] border border-border/70 bg-muted/30 p-3 text-sm text-muted-foreground">
                        This address is reachable, but may include an extended-area adjustment.
                      </div>
                    ) : null}

                    {feasibility?.serviceArea.zone === 'in_area' ? (
                      <InlineStatus icon="check">This address works.</InlineStatus>
                    ) : null}
                  </div>
                ) : null}

                <StepActions
                  backLabel={bookingTheme.flowStyle === 'address_first' ? null : 'Back'}
                  nextLabel={
                    locationStepNext === 'services' ? 'Choose a service' : 'See availability'
                  }
                  nextDisabled={
                    locationStepNext === 'services'
                      ? !selectedAddress
                      : !selectedAddress || !feasibility?.slotLookupAllowed
                  }
                  onBack={goBack}
                  onNext={() => setStep(locationStepNext)}
                  services={selectedServices}
                  totalPrice={totalPrice}
                  priceDisplay={selectedPriceDisplay}
                  date={selectedDate}
                  time={selectedTime}
                  surfaceStyle={bookingTheme.surfaceStyle}
                />
              </FlowPanel>
            ) : null}

            {renderedStep === 'schedule' ? (
              <FlowPanel
                kicker="Availability"
                title="Pick a day and time."
                surfaceStyle={bookingTheme.surfaceStyle}
              >
                <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {dateOptions.map((date) => (
                    <button
                      key={date.value}
                      type="button"
                      className={`cursor-pointer rounded-[8px] border p-3 text-left transition-colors ${
                        selectedDate === date.value
                          ? 'border-primary bg-primary/7'
                          : 'border-border/70 bg-card/60 hover:border-primary/45'
                      }`}
                      onClick={() => {
                        setSelectedDate(date.value)
                        setSelectedTime('')
                      }}
                    >
                      <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                        {date.weekday}
                      </p>
                      <p className="mt-1 text-lg font-semibold">{date.day}</p>
                      <p className="text-sm text-muted-foreground">{date.month}</p>
                    </button>
                  ))}
                </div>

                <div className="min-h-32 rounded-[10px] border border-border/70 bg-background/70 p-4">
                  {!selectedDate ? (
                    <EmptyState>Select a day.</EmptyState>
                  ) : feasibleSlotsQuery.isLoading ? (
                    <InlineStatus icon="loading">Loading available times...</InlineStatus>
                  ) : feasibleSlotsQuery.data?.result === 'slots_available' &&
                    feasibleSlotsQuery.data.slots.length > 0 ? (
                    <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-4">
                      {feasibleSlotsQuery.data.slots.map((slot) => (
                        <button
                          key={slot.startTime}
                          type="button"
                          className={`relative cursor-pointer rounded-[7px] border px-3 py-3 text-center text-sm font-medium transition-colors ${
                            selectedTime === slot.startTime
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border/70 bg-card/70 hover:border-primary/45'
                          }`}
                          onClick={() => setSelectedTime(slot.startTime)}
                        >
                          {slot.startTime}
                          {'score' in slot && (slot as { score?: number }).score != null ? (
                            <span className="mt-1 block text-[11px] opacity-70">Good fit</span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  ) : slotRecoveryState ? (
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="font-medium">{slotRecoveryState.title}</p>
                        <p className="mt-1 text-muted-foreground">
                          {slotRecoveryState.description}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => setSelectedDate('')}>
                          Try another day
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setStep('location')}>
                          Change address
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setStep('services')}>
                          Edit services
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <EmptyState>No times are available for this day.</EmptyState>
                  )}
                </div>

                <StepActions
                  backLabel="Back"
                  nextLabel="Continue"
                  nextDisabled={!selectedDate || !selectedTime}
                  onBack={goBack}
                  onNext={() => setStep('contact')}
                  services={selectedServices}
                  totalPrice={totalPrice}
                  priceDisplay={selectedPriceDisplay}
                  date={selectedDate}
                  time={selectedTime}
                  surfaceStyle={bookingTheme.surfaceStyle}
                />
              </FlowPanel>
            ) : null}

            {renderedStep === 'contact' ? (
              <FlowPanel
                kicker="Finish"
                title="Confirm your details."
                surfaceStyle={bookingTheme.surfaceStyle}
              >
                {serviceFormFields.length > 0 ? (
                  <div className="space-y-3 rounded-[10px] border border-border/70 bg-card/55 p-4">
                    <p className="text-sm font-medium">Service details</p>
                    {serviceFormFields.map((field: FormField) => (
                      <FormFieldRenderer
                        key={field.id}
                        field={field}
                        value={formResponses[field.id]}
                        onChange={(value) =>
                          setFormResponses((current) => ({ ...current, [field.id]: value }))
                        }
                      />
                    ))}
                  </div>
                ) : null}

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="customer-name">Name</Label>
                    <Input
                      id="customer-name"
                      value={customerName}
                      onChange={(event) => setCustomerName(event.target.value)}
                      placeholder="Your full name"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="customer-email">Email</Label>
                    <Input
                      id="customer-email"
                      type="email"
                      value={customerEmail}
                      onChange={(event) => setCustomerEmail(event.target.value)}
                      placeholder="Optional"
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customer-phone">Phone</Label>
                  <div className="flex gap-2">
                    <Input
                      id="customer-phone"
                      value={customerPhone}
                      onChange={(event) => {
                        const nextPhone = formatPhoneInput(event.target.value)
                        setCustomerPhone(nextPhone)
                        setOtpSent(false)
                        setVerificationToken('')
                        setOtpCode('')
                      }}
                      placeholder="(555) 555-5555"
                      className="h-11 flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!isCustomerPhoneValid || sendOTPMutation.isPending || otpSent}
                      onClick={handleSendOTP}
                    >
                      {sendOTPMutation.isPending ? 'Sending' : otpSent ? 'Sent' : 'Verify'}
                    </Button>
                  </div>
                  {!isCustomerPhoneValid && customerPhone ? (
                    <p className="text-sm text-destructive">
                      Enter a valid phone number, for example (415) 555-0148.
                    </p>
                  ) : null}
                  {sendOTPMutation.error ? (
                    <p className="text-sm text-destructive">{sendOTPMutation.error.message}</p>
                  ) : null}
                </div>

                {otpSent && !verificationToken ? (
                  <div className="space-y-2 rounded-[10px] border border-border/70 bg-card/55 p-4">
                    <Label htmlFor="otp-code">Verification code</Label>
                    <div className="flex gap-2">
                      <Input
                        id="otp-code"
                        value={otpCode}
                        onChange={(event) => setOtpCode(event.target.value)}
                        placeholder="6-digit code"
                        maxLength={6}
                        className="h-11 flex-1"
                      />
                      <Button
                        type="button"
                        disabled={otpCode.length !== 6 || verifyOTPMutation.isPending}
                        onClick={handleVerifyOTP}
                      >
                        {verifyOTPMutation.isPending ? 'Checking' : 'Confirm'}
                      </Button>
                    </div>
                    {verifyOTPMutation.error ? (
                      <p className="text-sm text-destructive">{verifyOTPMutation.error.message}</p>
                    ) : null}
                  </div>
                ) : null}

                <div className="space-y-1">
                  <Label htmlFor="booking-notes">Notes</Label>
                  <Textarea
                    id="booking-notes"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Anything helpful before arrival?"
                    rows={3}
                  />
                </div>

                {bookingPolicyItems.length > 0 ? (
                  <BookingPolicies items={bookingPolicyItems} />
                ) : null}

                <div className="flex items-center justify-between gap-4 rounded-[10px] border border-border/70 bg-card/55 p-4">
                  <div>
                    <Label>Flexible timing</Label>
                  </div>
                  <Switch checked={isFlexible} onCheckedChange={setIsFlexible} />
                </div>

                {verificationToken ? (
                  <InlineStatus icon="check">Phone verified.</InlineStatus>
                ) : null}

                <StepActions
                  backLabel="Back"
                  nextLabel={createAppointmentMutation.isPending ? 'Booking...' : 'Confirm booking'}
                  nextDisabled={
                    !customerName.trim() ||
                    !verificationToken ||
                    !requiredServiceFieldsComplete ||
                    createAppointmentMutation.isPending
                  }
                  onBack={goBack}
                  onNext={handleCreateAppointment}
                  services={selectedServices}
                  totalPrice={totalPrice}
                  priceDisplay={selectedPriceDisplay}
                  date={selectedDate}
                  time={selectedTime}
                  surfaceStyle={bookingTheme.surfaceStyle}
                />
                {!requiredServiceFieldsComplete ? (
                  <p className="text-sm text-muted-foreground">
                    Complete required service details before confirming.
                  </p>
                ) : null}
                {createAppointmentMutation.error ? (
                  <p className="text-sm text-destructive">
                    {createAppointmentMutation.error.message}
                  </p>
                ) : null}
              </FlowPanel>
            ) : null}

            {renderedStep === 'confirmed' && bookingResult ? (
              <BookingCompletion
                theme={bookingTheme}
                operator={operator}
                services={selectedServices}
                bookingResult={bookingResult}
                confirmationContent={confirmationContent}
                selectedAddress={selectedAddress?.displayName ?? null}
                customerPhone={customerPhone}
                customerEmail={customerEmail}
                onRebook={() => {
                  setStep('services')
                  resetSchedule()
                }}
              />
            ) : null}
          </div>

          <p className="pb-4 text-center text-xs text-muted-foreground">
            Secure booking powered by Zippity
          </p>
        </main>
      </div>
    </BookingCanvas>
  )
}

function PreviewRibbon() {
  return (
    <div className="flex w-full items-center justify-center gap-2 border-b border-primary/20 bg-primary/10 px-4 py-2 text-center text-xs font-semibold uppercase tracking-[0.14em] text-primary md:px-6">
      <ShieldCheck className="size-3" />
      Preview mode
    </div>
  )
}

function BookingCanvas({
  children,
  theme,
}: {
  children: React.ReactNode
  theme?: OperatorBookingPageTheme
}) {
  const resolvedTheme = normalizeOperatorBookingTheme(theme)
  const pageBackgroundImage = resolvedTheme.hero.backgroundImageUrl
  const themeStyle = {
    '--booking-accent': resolvedTheme.accentColor,
    '--primary': resolvedTheme.accentColor,
    '--ring': resolvedTheme.accentColor,
    '--booking-page-image': pageBackgroundImage
      ? `url(${JSON.stringify(pageBackgroundImage)})`
      : 'none',
  } as CSSProperties

  return (
    <div
      className={`min-h-screen overflow-x-hidden ${getBookingFontClass(
        resolvedTheme
      )} ${getBookingSurfaceClass(
        resolvedTheme.surfaceStyle
      )} ${getBookingFlagClass(resolvedTheme)}`}
      style={themeStyle}
    >
      {children}
    </div>
  )
}

function FlowPanel({
  kicker,
  title,
  description,
  children,
  surfaceStyle,
}: {
  kicker: string
  title: string
  description?: React.ReactNode
  children: React.ReactNode
  surfaceStyle: OperatorBookingPageTheme['surfaceStyle']
}) {
  return (
    <PremiumPanel className={`flex flex-1 overflow-hidden ${getBookingPanelClass(surfaceStyle)}`}>
      <CardContent className={`flex flex-1 flex-col ${getBookingPanelContentClass(surfaceStyle)}`}>
        <div className="max-w-2xl">
          <p className={getBookingKickerClass(surfaceStyle)}>{kicker}</p>
          <h2 className={getBookingStepTitleClass(surfaceStyle)}>{title}</h2>
          {description ? (
            <div className="mt-3 text-base leading-7 text-muted-foreground">{description}</div>
          ) : null}
        </div>
        {children}
      </CardContent>
    </PremiumPanel>
  )
}

function StepActions({
  backLabel,
  nextLabel,
  nextDisabled,
  onBack,
  onNext,
  services,
  totalPrice,
  priceDisplay,
  date,
  time,
  surfaceStyle,
}: {
  backLabel: string | null
  nextLabel: string
  nextDisabled: boolean
  onBack?: () => void
  onNext: () => void
  services: Service[]
  totalPrice: number
  priceDisplay: ServicePriceDisplay
  date: string
  time: string
  surfaceStyle: OperatorBookingPageTheme['surfaceStyle']
}) {
  const serviceLabel =
    services.length === 0
      ? 'Choose a service'
      : services.length === 1
        ? services[0]?.name
        : `${services.length} services selected`
  const priceLabel =
    totalPrice > 0 && priceDisplay !== 'hidden'
      ? `${priceDisplay === 'from' ? 'From ' : ''}$${(totalPrice / 100).toFixed(2)}`
      : null
  const timeLabel = date && time ? `${formatDateLabel(date)} at ${time}` : null
  const hasSummary = services.length > 0 || Boolean(timeLabel) || Boolean(priceLabel)

  return (
    <div className="mt-auto grid gap-3 border-t border-border/70 pt-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
      {hasSummary ? (
        <div
          className={`border border-border/70 bg-background/78 p-3 ${getBookingInsetClass(
            surfaceStyle
          )}`}
        >
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {services.length > 0 ? (
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Booking
                </p>
                <p className="mt-1 truncate text-sm font-semibold">{serviceLabel}</p>
              </div>
            ) : null}
            {timeLabel ? (
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Time
                </p>
                <p className="mt-1 truncate text-sm font-medium">{timeLabel}</p>
              </div>
            ) : null}
            {priceLabel ? <div className="text-sm font-semibold">{priceLabel}</div> : null}
          </div>
        </div>
      ) : (
        <span aria-hidden="true" />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 md:justify-end">
        {backLabel ? (
          <Button type="button" variant="ghost" onClick={onBack}>
            <ArrowLeft className="size-4" />
            {backLabel}
          </Button>
        ) : (
          <span />
        )}
        <Button type="button" size="lg" disabled={nextDisabled} onClick={onNext}>
          {nextLabel}
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </div>
  )
}

function InlineStatus({
  icon,
  children,
}: {
  icon: 'loading' | 'check'
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      {icon === 'loading' ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Check className="size-4 text-primary" />
      )}
      {children}
    </div>
  )
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[10px] border border-dashed border-border/70 bg-muted/20 p-5 text-sm text-muted-foreground">
      {children}
    </div>
  )
}

function BookingPolicies({ items }: { items: Array<{ label: string; value: string }> }) {
  return (
    <div className="space-y-3 rounded-[10px] border border-border/70 bg-card/55 p-4">
      <div className="flex items-center gap-2">
        <ClipboardList className="size-4 text-primary" />
        <p className="text-sm font-semibold">Booking notes</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((item) => (
          <div
            key={item.label}
            className="rounded-[8px] border border-border/60 bg-background/65 p-3"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {item.label}
            </p>
            <MarkdownPreview markdown={item.value} className="mt-1" />
          </div>
        ))}
      </div>
    </div>
  )
}

function FormFieldRenderer({
  field,
  value,
  onChange,
}: {
  field: FormField
  value: unknown
  onChange: (val: unknown) => void
}) {
  const parsedOptions = field.options ?? []

  return (
    <div className="space-y-2">
      <Label>
        {field.label}
        {field.isRequired ? <span className="text-destructive"> *</span> : null}
      </Label>
      {field.helpText ? <p className="text-xs text-muted-foreground">{field.helpText}</p> : null}

      {field.fieldType === 'text' ? (
        <Input
          value={(value as string) || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder || ''}
        />
      ) : null}

      {field.fieldType === 'textarea' ? (
        <Textarea
          value={(value as string) || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder || ''}
          rows={3}
        />
      ) : null}

      {field.fieldType === 'number' ? (
        <Input
          type="number"
          value={(value as string) || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder || ''}
        />
      ) : null}

      {field.fieldType === 'phone' ? (
        <Input
          type="tel"
          value={(value as string) || ''}
          onChange={(event) => onChange(formatPhoneInput(event.target.value))}
          placeholder={field.placeholder || '(555) 555-5555'}
        />
      ) : null}

      {field.fieldType === 'email' ? (
        <Input
          type="email"
          value={(value as string) || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder || 'email@example.com'}
        />
      ) : null}

      {field.fieldType === 'select' ? (
        <select
          value={(value as string) || ''}
          onChange={(event) => onChange(event.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="">{field.placeholder || 'Select...'}</option>
          {parsedOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : null}

      {field.fieldType === 'multi_select' ? (
        <div className="flex flex-wrap gap-2">
          {parsedOptions.map((option) => {
            const selected = Array.isArray(value) && (value as string[]).includes(option)
            return (
              <Button
                key={option}
                type="button"
                variant={selected ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  const current = Array.isArray(value) ? (value as string[]) : []
                  onChange(
                    selected
                      ? current.filter((currentOption) => currentOption !== option)
                      : [...current, option]
                  )
                }}
              >
                {option}
              </Button>
            )
          })}
        </div>
      ) : null}

      {field.fieldType === 'yes_no' ? (
        <div className="flex gap-2">
          <Button
            type="button"
            variant={value === true ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange(true)}
          >
            Yes
          </Button>
          <Button
            type="button"
            variant={value === false ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange(false)}
          >
            No
          </Button>
        </div>
      ) : null}

      {field.fieldType === 'photo' ? (
        <Input
          type="file"
          accept="image/*"
          onChange={(event) => onChange(event.target.files?.[0]?.name || '')}
        />
      ) : null}
    </div>
  )
}

function getBookingPageLoadErrorCopy(error: unknown): { title: string; description: string } {
  const status =
    typeof error === 'object' && error !== null && 'data' in error
      ? (error.data as { httpStatus?: unknown } | undefined)?.httpStatus
      : undefined

  if (error && status !== 404) {
    return {
      title: 'Booking is temporarily unavailable',
      description:
        'The booking system is not available right now. Please try again shortly or contact the business directly.',
    }
  }

  return {
    title: 'Booking page not found',
    description: 'This booking link is invalid or no longer active.',
  }
}

function buildDateOptions(count: number) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })

  return Array.from({ length: count }, (_, index) => {
    const date = new Date()
    date.setDate(date.getDate() + index)
    const parts = formatter.formatToParts(date)
    return {
      value: date.toISOString().split('T')[0],
      weekday: parts.find((part) => part.type === 'weekday')?.value ?? '',
      month: parts.find((part) => part.type === 'month')?.value ?? '',
      day: parts.find((part) => part.type === 'day')?.value ?? '',
    }
  })
}

function formatDateLabel(date: string) {
  const parsed = new Date(`${date}T12:00:00`)
  if (Number.isNaN(parsed.getTime())) return date

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(parsed)
}

function getBusinessInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

function getBookingFontClass(theme: OperatorBookingPageTheme) {
  if (theme.surfaceStyle === 'industrial') return 'booking-font-industrial'
  if (theme.surfaceStyle === 'modern') return 'booking-font-modern'
  if (theme.surfaceStyle === 'editorial') return 'booking-font-editorial'
  if (theme.fontStyle === 'modern') return 'booking-font-modern'
  if (theme.fontStyle === 'editorial') return 'booking-font-editorial'
  return 'booking-font-classic'
}

function getBookingSurfaceClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'modern') return 'booking-surface-modern'
  if (surfaceStyle === 'industrial') return 'booking-surface-industrial'
  if (surfaceStyle === 'editorial') return 'booking-surface-editorial'
  return 'booking-surface-classic'
}

function getBookingFlagClass(theme: OperatorBookingPageTheme) {
  return [
    `booking-flow-${theme.flowStyle.replace('_', '-')}`,
    `booking-layout-${theme.pageLayout}`,
    `booking-cards-${theme.serviceCardStyle}`,
    `booking-overlay-${theme.hero.overlayStyle}`,
    theme.hero.backgroundImageUrl ? 'booking-has-page-image' : 'booking-no-page-image',
    theme.hero.bannerImageUrl ? 'booking-has-banner-image' : 'booking-no-banner-image',
    theme.showDuration ? 'booking-shows-duration' : 'booking-hides-duration',
  ].join(' ')
}

function getBookingHeaderClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[3px] shadow-none'
  if (surfaceStyle === 'editorial') return 'rounded-[20px] shadow-sm'
  if (surfaceStyle === 'modern') return 'rounded-[12px] shadow-sm'
  return 'rounded-[10px] shadow-sm'
}

function getBookingPanelClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[3px] border-foreground/20 shadow-none'
  if (surfaceStyle === 'editorial') return 'rounded-[22px] shadow-sm'
  if (surfaceStyle === 'modern') return 'rounded-[12px] shadow-sm'
  return 'rounded-[10px] shadow-sm'
}

function getBookingPanelContentClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'gap-5 p-4 md:p-5'
  if (surfaceStyle === 'editorial') return 'gap-7 p-6 md:p-8'
  return 'gap-6 p-5 md:p-7'
}

function getBookingInsetClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[2px]'
  if (surfaceStyle === 'editorial') return 'rounded-[16px]'
  if (surfaceStyle === 'modern') return 'rounded-[10px]'
  return 'rounded-[8px]'
}

function getBookingSmallSurfaceClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[2px]'
  if (surfaceStyle === 'editorial') return 'rounded-[12px]'
  if (surfaceStyle === 'modern') return 'rounded-[8px]'
  return 'rounded-[6px]'
}

function getBookingAvatarClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[2px]'
  if (surfaceStyle === 'editorial') return 'rounded-[16px]'
  if (surfaceStyle === 'modern') return 'rounded-[10px]'
  return 'rounded-[8px]'
}

function getBookingKickerClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') {
    return 'mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-primary'
  }
  return 'mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary'
}

function getBookingStepTitleClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') {
    return 'text-2xl font-semibold uppercase tracking-[0.06em] md:text-3xl'
  }
  if (surfaceStyle === 'editorial') {
    return 'text-4xl font-semibold tracking-tight md:text-5xl'
  }
  return 'text-3xl font-semibold tracking-tight md:text-4xl'
}

function getStepOrder(flowStyle: OperatorBookingPageTheme['flowStyle']) {
  return STEP_ORDER_BY_FLOW[flowStyle]
}

function getNextStep(
  step: Exclude<BookingStep, 'confirmed'>,
  stepOrder: Exclude<BookingStep, 'confirmed'>[]
) {
  const index = stepOrder.indexOf(step)
  return stepOrder[Math.min(index + 1, stepOrder.length - 1)]
}

function resolveSelectedPriceDisplay(
  services: Service[],
  displayGroupById: Map<string, DisplayGroupBookingPageConfig>
): ServicePriceDisplay {
  if (services.length === 0) return 'standard'

  const priceDisplays = services.map((service) =>
    resolveServicePriceDisplay(
      normalizeServiceBookingConfig(service.bookingPageConfig),
      service.displayGroupId ? displayGroupById.get(service.displayGroupId) : null
    )
  )

  if (priceDisplays.every((priceDisplay) => priceDisplay === 'hidden')) return 'hidden'
  if (priceDisplays.some((priceDisplay) => priceDisplay === 'from')) return 'from'
  return 'standard'
}
